/**
 * Consumer API client instance for apps/web.
 *
 * Creates a singleton `ConsumerApi` wired to:
 *   - VITE_API_URL env var for base URLs (per-environment)
 *   - localStorage for token persistence (matches OS app pattern; flagged for
 *     CJIS/SOC2 review — may move to httpOnly cookies post-launch)
 *   - An injectable `onUnauthorized` hook so AuthContext can redirect on
 *     session-expiry, without creating a circular dep between the client and
 *     the React tree.
 *
 * The client is created once on module load. AuthContext registers its
 * `onUnauthorized` callback via `setOnUnauthorized()` after the Router mounts.
 */

import { createConsumerApiClient, type ConsumerApi } from '@pelipost/consumer-api-client';

const STORAGE_KEYS = {
  accessToken: 'pelipost.web.accessToken',
  refreshToken: 'pelipost.web.refreshToken',
} as const;

const apiBase = import.meta.env.VITE_API_URL;
if (!apiBase) {
  throw new Error(
    '[apps/web] VITE_API_URL is not defined. Set it in .env.development / .env.staging / .env.production.',
  );
}

const mobileBaseUrl = `${apiBase}/api/v1/mobile`;
const rootBaseUrl = `${apiBase}/api/v1`;

// Registered by AuthContext at mount time. Before then (e.g. a very-early 401
// during bootstrap) we simply no-op; the request will throw and AuthContext
// will pick it up on mount.
let onUnauthorizedHandler: (() => void) | null = null;

export function setOnUnauthorized(handler: (() => void) | null): void {
  onUnauthorizedHandler = handler;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.accessToken);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.refreshToken);
}

export function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
  localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
}

export const api: ConsumerApi = createConsumerApiClient({
  mobileBaseUrl,
  rootBaseUrl,
  getToken: async () => getAccessToken(),
  getRefreshToken: async () => getRefreshToken(),
  setToken: async (token) => {
    localStorage.setItem(STORAGE_KEYS.accessToken, token);
  },
  setRefreshToken: async (token) => {
    localStorage.setItem(STORAGE_KEYS.refreshToken, token);
  },
  clearAuth: async () => {
    clearTokens();
  },
  onUnauthorized: () => {
    if (onUnauthorizedHandler) {
      onUnauthorizedHandler();
    }
  },
});
